import { AxiosInstance } from 'axios';
import { apiBaseUrl } from '../../api/baseUrl';
import { getIssueOverview } from '../../api/issues';
import { getRolloutOverview } from '../../api/rollout';

/**
 * The /Cockpit endpoints of the v2 dashboard. Mirrors the DTOs of CockpitController on the server.
 * The shared axios instance hands JSON over as text, as the generated clients expect.
 */

const body = <T>(data: unknown): T =>
    (typeof data === 'string' && data.length > 0 ? JSON.parse(data) : data) as T;

const seg = (value: string) => encodeURIComponent(value);

export type EnvKind = 'prod' | 'test' | 'lab';
export type Lane = 'cb' | 'cn' | 'ex';
export type FlowApp = 'cbrcv' | 'exsnd' | 'exrcv' | 'cbsnd';
export type FlowState = 'ok' | 'bad' | 'wait' | 'busy';
export type StepState = 'ok' | 'bad' | 'wait' | 'todo';
export type Tone = 'ok' | 'warn' | 'bad' | 'info';

export interface AppBuild {
    app: string;
    build: string;
}

/** One customer environment with everything the overview screens show. */
export interface CockpitEnvironment {
    customer: string;
    environment: string;
    stage: 'prod' | 'test';
    kind: EnvKind;
    open: number;
    waitingOnUs: number;
    waitingOnCustomer: number;
    resolvesItself: number;
    needsRequeue: number;
    oldestOpenAt?: string | null;
    latestOpenFailureAt?: string | null;
    exactRemaining?: number | null;
    exactLimit?: number | null;
    exactResetAt?: string | null;
    exactUsedLastHour?: number | null;
    exactRunsOutAt?: string | null;
    queueChargebee?: number | null;
    queueExact?: number | null;
    poison?: number | null;
    postponed?: number | null;
    builds: AppBuild[];
    build?: string | null;
    behind?: boolean | null;
    watchStatus?: 'watching' | 'done' | 'stopped' | null;
    newProblems?: number | null;
    lastSuccessAt?: string | null;
}

export interface PostponedReason {
    reason: string;
    reasonCode?: string | null;
    count: number;
    nextAttemptAt?: string | null;
}

export interface Postponed {
    total: number;
    reasons: PostponedReason[];
    items: { objectType: string; relationId: string; reason: string; nextAttemptAt?: string | null; attempts: number }[];
}

export interface SearchHit {
    customer: string;
    environment: string;
    kind: EnvKind;
    objectType: string;
    relationId: string;
    label: string;
    issueKey?: string | null;
    issueTitle?: string | null;
    status?: string | null;
    owner?: string | null;
}

export interface FollowState {
    chargebee: StepState;
    connector: StepState;
    exact: StepState;
    text: string;
    issueKey?: string | null;
    issueTitle?: string | null;
}

export interface Follow {
    id: string;
    customer: string;
    environment: string;
    objectType: string;
    relationId: string;
    label: string;
    followedAt: string;
    state: FollowState;
}

export type RequeueVerdict = 'may' | 'auto' | 'no' | 'na' | 'unknown';

export interface RequeuePreview {
    customer: string;
    environment: string;
    key: string;
    verdict: RequeueVerdict;
    reason?: string | null;
    nextAt?: string | null;
}

export interface ActivityItem {
    id: string;
    at: string;
    /** Empty for a line about the dashboard itself, such as a finished scan round. */
    customer?: string | null;
    environment?: string | null;
    system: 'cb' | 'cn' | 'ex' | 'lab' | 'dashboard';
    tone: Tone;
    text: string;
    objectType?: string | null;
    relationId?: string | null;
}

export interface TimelineDocument {
    id: string;
    objectType: string;
    relationId: string;
    label: string;
}

export interface TimelineEvent {
    at?: string | null;
    documentId: string;
    lane: Lane;
    app?: FlowApp | null;
    state: FlowState;
    title: string;
    detail?: string | null;
    /** Client only: happened in its own system without the connector, such as matching by hand in Exact. */
    direct?: boolean;
    /** Client only: Exact gives the day, not the time. */
    dayOnly?: boolean;
}

export interface Timeline {
    documents: TimelineDocument[];
    events: TimelineEvent[];
}

export interface Automation {
    lastHour: {
        rounds?: number | null;
        checked?: number | null;
        resolved?: number | null;
        refetched?: number | null;
        requeued?: number | null;
        exactCalls?: number | null;
    };
    upcoming: { at: string; text: string; customer?: string | null; environment?: string | null }[];
}

/** One hour of Exact calls; mirrors ExactUsageHourDto. */
export interface ExactUsageHour {
    hour: string;
    /** The dashboard's own calls per source: sweep, reevaluation, rolloutwatch:…, liveread, screen, other. */
    dashboard?: Record<string, number> | null;
    dashboardCalls: number;
    throttled: number;
    connectorLogged: number;
    remainingLow?: number | null;
    usedInferred?: number | null;
    connectorInferred?: number | null;
}

export interface ExactUsage {
    remaining?: number | null;
    limit?: number | null;
    resetAt?: string | null;
    usedLastHour?: number | null;
    runsOutAt?: string | null;
    connectorError?: string | null;
    hours: ExactUsageHour[];
}

export const cockpitHubUrl = `${apiBaseUrl.replace(/\/+$/, '')}/hubs/cockpit`;
export const ACTIVITY_METHOD = 'activity';

export const getCockpitEnvironments = async (axios: AxiosInstance, signal?: AbortSignal): Promise<CockpitEnvironment[]> =>
    body<CockpitEnvironment[]>((await axios.get('/Cockpit/environments', { signal })).data) ?? [];

/**
 * The environments from what the API had before /Cockpit existed: the issue overview and the rollout
 * overview. Queue depths, projections and the lab are missing then; the screens say so.
 */
export const getEnvironmentsFallback = async (axios: AxiosInstance, signal?: AbortSignal): Promise<CockpitEnvironment[]> => {
    const [issues, rollout] = await Promise.all([
        getIssueOverview(axios, signal),
        getRolloutOverview(axios).catch(() => []),
    ]);
    const key = (c: string, e: string) => `${c}/${e}`.toLowerCase();
    const rows = new Map(rollout.map((r) => [key(r.customer, r.environment), r]));

    return issues.map((i) => {
        const r = rows.get(key(i.customer, i.environment));
        const builds = (r?.buildNumbers ?? '')
            .split(';')
            .map((part) => part.split('='))
            .filter((p) => p.length === 2 && p[1])
            .map(([app, build]) => ({ app, build }));

        return {
            customer: i.customer,
            environment: i.environment,
            stage: i.stage,
            kind: i.stage,
            open: i.open,
            waitingOnUs: i.waitingOnUs,
            waitingOnCustomer: i.waitingOnCustomer,
            resolvesItself: i.resolvesItself,
            needsRequeue: i.needsRequeue,
            oldestOpenAt: i.oldestOpenAt,
            latestOpenFailureAt: i.latestOpenFailureAt,
            exactRemaining: r?.exactDailyRemaining ?? i.exactRemaining,
            exactLimit: r?.exactDailyLimit ?? i.exactLimit,
            exactResetAt: i.exactResetAt,
            queueExact: null,
            queueChargebee: i.exactWaiting ?? null,
            poison: i.exactPoison ?? null,
            builds,
            build: builds[0]?.build ?? null,
            watchStatus: r?.watchStatus ?? null,
            newProblems: r?.newProblems ?? null,
            lastSuccessAt: i.lastSuccessAt,
        } as CockpitEnvironment;
    });
};

export const getPostponed = async (axios: AxiosInstance, customer: string, environment: string, signal?: AbortSignal): Promise<Postponed> =>
    body<Postponed>((await axios.get(`/Cockpit/${seg(customer)}/${seg(environment)}/postponed`, { signal })).data);

export const getExactUsage = async (axios: AxiosInstance, customer: string, environment: string, hours = 24, signal?: AbortSignal): Promise<ExactUsage> =>
    body<ExactUsage>((await axios.get(`/Cockpit/${seg(customer)}/${seg(environment)}/exact-usage`, { params: { hours }, signal })).data);

export const searchEverywhere = async (axios: AxiosInstance, q: string, signal?: AbortSignal): Promise<SearchHit[]> =>
    body<{ hits: SearchHit[] }>((await axios.get('/Cockpit/search', { params: { q, limit: 20 }, signal })).data)?.hits ?? [];

export const getFollows = async (axios: AxiosInstance, signal?: AbortSignal): Promise<Follow[]> =>
    body<Follow[]>((await axios.get('/Cockpit/follows', { signal })).data) ?? [];

export const follow = async (
    axios: AxiosInstance,
    item: { customer: string; environment: string; objectType: string; relationId: string; label: string }
): Promise<Follow> =>
    body<Follow>((await axios.put('/Cockpit/follows', item, { headers: { 'Content-Type': 'application/json' } })).data);

export const unfollow = async (axios: AxiosInstance, id: string): Promise<void> => {
    await axios.delete(`/Cockpit/follows/${seg(id)}`);
};

export const previewRequeue = async (
    axios: AxiosInstance,
    items: { customer: string; environment: string; key: string }[],
    signal?: AbortSignal
): Promise<RequeuePreview[]> =>
    body<RequeuePreview[]>(
        (await axios.post('/Cockpit/requeue/preview', { items }, { headers: { 'Content-Type': 'application/json' }, signal })).data
    ) ?? [];

/** Offers open issues again through the same guards as an approved proposal; writes. */
export const requeueIssues = async (
    axios: AxiosInstance,
    items: { customer: string; environment: string; key: string }[]
): Promise<{ item: { customer: string; environment: string; key: string }; state: string; note?: string | null }[]> =>
    body((await axios.post('/Cockpit/requeue', { items }, { headers: { 'Content-Type': 'application/json' } })).data) ?? [];

export const getActivity = async (axios: AxiosInstance, since?: string, signal?: AbortSignal): Promise<ActivityItem[]> =>
    body<ActivityItem[]>((await axios.get('/Cockpit/activity', { params: { since, limit: 50 }, signal })).data) ?? [];

export const getTimeline = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    by: { key?: string; objectType?: string; relationId?: string },
    signal?: AbortSignal
): Promise<Timeline> =>
    body<Timeline>((await axios.get(`/Cockpit/${seg(customer)}/${seg(environment)}/timeline`, { params: by, signal })).data);

export const getAutomation = async (axios: AxiosInstance, signal?: AbortSignal): Promise<Automation> =>
    body<Automation>((await axios.get('/Cockpit/automation', { signal })).data);
